import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getLanguageFromFile } from '../utils/fileUtils';
import './CodePreview.css';

/**
 * Simple code preview component with syntax highlighting
 * @param {Object} props - Component props
 * @param {string} props.content - Code content
 * @param {string} props.language - Programming language
 * @param {number} props.maxHeight - Maximum height in pixels
 */
function CodePreview({ content, language, maxHeight }) {
  const preRef = useRef(null);
  
  // Apply syntax highlighting when content changes
  useEffect(() => {
    const highlightCode = async () => {
      if (!content || !preRef.current) return;
      
      try {
        // Check if Prism is already loaded
        if (window.Prism) {
          highlightWithPrism();
        } else {
          // Load Prism.js dynamically
          await loadPrism();
          highlightWithPrism();
        }
      } catch (error) {
        console.error('Error applying syntax highlighting:', error);
      }
    };
    
    highlightCode();
  }, [content, language]);
  
  // Load Prism.js dynamically
  const loadPrism = async () => {
    return new Promise((resolve) => {
      // Create Prism script tag
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.27.0/prism.min.js';
      script.async = true;
      
      // Create Prism CSS tag
      const style = document.createElement('link');
      style.href = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.27.0/themes/prism.min.css';
      style.rel = 'stylesheet';
      
      // Resolve when script is loaded
      script.onload = () => {
        // Load language components
        loadLanguageComponents().then(resolve);
      };
      
      // Append to document head
      document.head.appendChild(style);
      document.head.appendChild(script);
    });
  };
  
  // Load additional language components
  const loadLanguageComponents = async () => {
    const langs = ['javascript', 'css', 'typescript', 'jsx', 'tsx', 'python', 'java', 'csharp', 'cpp'];
    
    const promises = langs.map((lang) => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = `https://cdnjs.cloudflare.com/ajax/libs/prism/1.27.0/components/prism-${lang}.min.js`;
        script.async = true;
        script.onload = resolve;
        document.head.appendChild(script);
      });
    });
    
    await Promise.all(promises);
  };
  
  // Apply Prism highlighting
  const highlightWithPrism = () => {
    if (window.Prism && preRef.current) {
      // Get normalized language
      const normalizedLanguage = getLanguageFromFile(language || 'txt');
      
      // Add language class to pre element
      preRef.current.className = `language-${normalizedLanguage}`;
      
      // Highlight element
      window.Prism.highlightElement(preRef.current);
    }
  };
  
  return (
    <div className="code-preview" style={{ maxHeight: maxHeight ? `${maxHeight}px` : 'auto' }}>
      <pre ref={preRef} className={`language-${getLanguageFromFile(language || 'txt')}`}>
        <code>{content || '// No content to display'}</code>
      </pre>
    </div>
  );
}

CodePreview.propTypes = {
  content: PropTypes.string,
  language: PropTypes.string,
  maxHeight: PropTypes.number
};

CodePreview.defaultProps = {
  maxHeight: 400
};

export default CodePreview;
